import {
  EmptyState,
  Link,
  PageHeader,
  PageLayout,
  Stack,
} from "@softmax/design-system";
import type { ReactNode } from "react";
import {
  PolicyVersionIdentity,
  type PolicyVersionIdentityProps,
} from "../identity/policy-version-identity";
import { PolicyVersionIdentitySkeleton } from "../identity/policy-version-identity-skeleton";
import { PolicyVersionOverview } from "../overview/policy-version-overview";
import type { PolicyVersionOverviewProps } from "../overview/policy-version-overview";
import { PolicyStateFeedback, type PolicyResourceState } from "../policy-state";

export type PolicyDetailState =
  | {
      readonly status: "empty";
      readonly title: string;
      readonly message: string;
    }
  | PolicyResourceState<{
      readonly identity: PolicyVersionIdentityProps;
      readonly versionPicker?: ReactNode;
      readonly selectionAccess?: ReactNode;
      readonly overview?: PolicyVersionOverviewProps | undefined;
      readonly staffJobs?: ReactNode;
    }>;
export type PolicyDetailScreenProps = {
  readonly state: PolicyDetailState;
  readonly back: { readonly href: string; readonly label: string };
  readonly onRetry?: (() => void) | undefined;
  readonly layout?: "page" | "workspace";
  readonly section?:
    | "overview"
    | "activity"
    | "episodes"
    | "access"
    | "diagnostics";
  readonly sectionNavigation?: ReactNode;
};

/** The shared detail composition keeps owner controls and independently loaded staff jobs beside the selected version. */
export function PolicyDetailScreen({
  state,
  back,
  onRetry,
  layout = "page",
  section,
  sectionNavigation,
}: PolicyDetailScreenProps) {
  return (
    <PageLayout variant={layout}>
      {state.status === "loading" && <PolicyVersionIdentitySkeleton />}
      {state.status === "error" && (
        <>
          <PageHeader
            variant={layout}
            title="Policy unavailable"
            actions={
              <Link focus="native" href={back.href}>
                {back.label}
              </Link>
            }
          />
          <PolicyStateFeedback
            state={state}
            subject="policy"
            onRetry={onRetry}
          />
        </>
      )}
      {state.status === "empty" && (
        <>
          <PageHeader variant={layout} title={state.title} />
          <EmptyState
            size={layout === "workspace" ? "compact" : "default"}
            title="No policy versions"
          >
            {state.message}
          </EmptyState>
          <Link focus="native" href={back.href}>
            {back.label}
          </Link>
        </>
      )}
      {state.status === "ready" && (
        <Stack width="auto" gap={24}>
          <PolicyVersionIdentity
            layout={layout}
            {...state.identity}
            versionSelector={
              state.versionPicker ?? state.identity.versionSelector
            }
          />
          {(state.refresh || onRetry) && (
            <PolicyStateFeedback
              state={state}
              subject="policy"
              onRetry={onRetry}
            />
          )}
          {sectionNavigation}
          <div hidden={section !== undefined && section !== "access"}>
            {state.selectionAccess ??
              (section === "access" ? (
                <EmptyState title="Selection access unavailable">
                  Only the policy owner can manage selection access.
                </EmptyState>
              ) : null)}
          </div>
          {state.overview && (
            <PolicyVersionOverview {...state.overview} section={section} />
          )}
          <div hidden={section !== undefined && section !== "diagnostics"}>
            {state.staffJobs}
          </div>
        </Stack>
      )}
    </PageLayout>
  );
}
